import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

const ParentActivation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const parentId = searchParams.get('parentId') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const isInvalidLink = useMemo(() => !token || !parentId, [token, parentId]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/activate-parent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          parentId,
          password
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Activation failed.');
      }

      await signInWithEmailAndPassword(auth, data.email, password);
      setSuccess('Account activated successfully. Redirecting...');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      setError(err.message || 'Could not activate parent account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1f2e] py-12 px-4">
      <div className="max-w-md w-full bg-[#313643] rounded-xl shadow-lg p-8 text-white">
        <h1 className="text-2xl font-bold text-center mb-2">Activate Parent Account</h1>
        <p className="text-center text-gray-300 mb-6">Create your password to complete activation.</p>

        {isInvalidLink ? (
          <div className="bg-red-500/15 border border-red-400 rounded-lg p-4 text-sm">
            Activation link is invalid. Please request a new invitation from your school manager.
          </div>
        ) : (
          <form onSubmit={handleActivate} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#272b35] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                required
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#272b35] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                required
              />
            </div>

            {error && <div className="text-red-300 text-sm">{error}</div>}
            {success && <div className="text-green-300 text-sm">{success}</div>}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 px-4 rounded-lg font-semibold bg-[#00E0E0] text-[#1a1f2e] hover:bg-[#00b8b8] transition-colors ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Activating...' : 'Activate Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ParentActivation;
